import React, { useState, useEffect } from 'react';
import DatePicker from '../Common/DatePicker';
import TimePicker from '../Common/TimePicker';
import { getLessons, createLesson, updateLesson, deleteLesson, uploadMaterials, deleteMaterial, reorderLessons } from '../../services/adminService';
import Modal from '../Common/Modal';
import DragHandle from '../Common/DragHandle';
import TimecodeEditor from './TimecodeEditor';
import useDragSort from '../../hooks/useDragSort';
import { confirmDialog } from '../Common/confirm';
import { FileIcon, fileColor } from '../Common/fileKind';
import toast from 'react-hot-toast';

const MONTHS = ['ЯНВ','ФЕВ','МАР','АПР','МАЙ','ИЮН','ИЮЛ','АВГ','СЕН','ОКТ','НОЯ','ДЕК'];
const DAYS   = ['Вс','Пн','Вт','Ср','Чт','Пт','Сб'];

const IcoEdit = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const IcoTrash = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/>
  </svg>
);

const IcoCalendar = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const DateBadge = ({ dateStr }) => {
  if (!dateStr) {
    return (
      <div style={{
        width: 46, height: 50, borderRadius: 10,
        background: 'rgba(124,77,255,0.08)', color: 'var(--text-3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <IcoCalendar />
      </div>
    );
  }
  const d = new Date(dateStr);
  return (
    <div style={{
      width: 46, height: 50, borderRadius: 10,
      background: 'rgba(124,77,255,0.15)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0,
    }}>
      <span style={{ fontSize: 18, fontWeight: 800, color: '#7c4dff', lineHeight: 1 }}>{d.getDate()}</span>
      <span style={{ fontSize: 9, fontWeight: 700, color: '#7c4dff', marginTop: 2 }}>{MONTHS[d.getMonth()]}</span>
    </div>
  );
};

const LessonsManager = ({ sectionId, onUpdate }) => {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [formData, setFormData] = useState({
    title: '', description: '', recordLink: '', date: '', time: '', duration: '', materials: []
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => { if (sectionId) loadLessons(); }, [sectionId]);

  const loadLessons = async () => {
    try {
      setLoading(true);
      const response = await getLessons(sectionId);
      setLessons(response.data || []);
    } catch {
      toast.error('Ошибка загрузки уроков');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingLesson(null);
    setFormData({
      title: '', description: '', recordLink: '',
      date: new Date().toISOString().split('T')[0], time: '18:00', duration: '', materials: [], isHomeworkOnly: false, timecodes: []
    });
    setModalOpen(true);
  };

  const handleEdit = (lesson) => {
    setEditingLesson(lesson);
    setFormData({
      title: lesson.title || '',
      description: lesson.description || '',
      recordLink: lesson.recordLink || '',
      date: lesson.date || new Date().toISOString().split('T')[0],
      time: lesson.time || '18:00',
      duration: lesson.duration ?? '',
      materials: lesson.materials || [],
      isHomeworkOnly: lesson.isHomeworkOnly || false,
      timecodes: lesson.timecodes || []
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (await confirmDialog({ title: 'Удалить урок?', message: 'Урок и его материалы будут удалены. Действие необратимо.', confirmText: 'Удалить' })) {
      try {
        await deleteLesson(id);
        toast.success('Урок удален');
        loadLessons();
        if (onUpdate) onUpdate();
      } catch {
        toast.error('Ошибка при удалении');
      }
    }
  };

  const handleFileUpload = async (files) => {
    if (!editingLesson) { toast.error('Сначала сохраните урок'); return; }
    setUploading(true);
    const fd = new FormData();
    for (let f of files) fd.append('materials', f);
    try {
      const response = await uploadMaterials(editingLesson.id, fd);
      toast.success('Файлы загружены');
      setFormData(prev => ({ ...prev, materials: [...(prev.materials || []), ...response.data] }));
      loadLessons();
    } catch {
      toast.error('Ошибка загрузки файлов');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveMaterial = async (index) => {
    if (!editingLesson) return;
    try {
      await deleteMaterial(editingLesson.id, index);
      toast.success('Файл удален');
      setFormData(prev => ({ ...prev, materials: prev.materials.filter((_, i) => i !== index) }));
      loadLessons();
    } catch {
      toast.error('Ошибка при удалении файла');
    }
  };

  const handleSubmit = async () => {
    if (!formData.title) { toast.error('Введите название урока'); return; }
    // Пустую длительность шлём как null, иначе — число
    const payload = {
      ...formData,
      sectionId,
      duration: formData.duration === '' || formData.duration == null ? null : Number(formData.duration),
      // сохраняем только время+название, отсортированные; служебный id убираем
      timecodes: (formData.timecodes || [])
        .map(c => ({ t: Number(c.t) || 0, title: (c.title || '').trim() }))
        .sort((a, b) => a.t - b.t),
    };
    try {
      if (editingLesson) {
        await updateLesson(editingLesson.id, payload);
        toast.success('Урок обновлен');
      } else {
        await createLesson(payload);
        toast.success('Урок создан');
      }
      setModalOpen(false);
      loadLessons();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('createLesson error:', err?.response?.data || err?.message || err);
      const msg = err?.response?.data?.message || err?.message || 'Ошибка при сохранении';
      toast.error(msg);
    }
  };

  // Перетаскивание уроков внутри модуля (за ручку, с анимацией)
  const { getHandleProps, getItemProps } = useDragSort(
    lessons,
    (ids) => setLessons(prev => ids.map(id => prev.find(l => l.id === id)).filter(Boolean)),
    async (ids) => { try { await reorderLessons(ids); if (onUpdate) onUpdate(); } catch { toast.error('Не удалось сохранить порядок'); loadLessons(); } }
  );

  if (loading) return (
    <div style={{ padding: '16px 0', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>Загрузка уроков...</div>
  );

  return (
    <div>
      {lessons.length === 0 && (
        <div style={{ padding: '16px 8px', color: 'var(--text-3)', fontSize: 13, textAlign: 'center' }}>
          В этом разделе пока нет уроков
        </div>
      )}

      {lessons.map((lesson, idx) => {
        const d = lesson.date ? new Date(lesson.date) : null;
        const timeStr = lesson.time ? lesson.time.slice(0, 5) : '';
        const dayLabel = d ? `${DAYS[d.getDay()]} · ${timeStr}${timeStr ? ' МСК' : ''}`.trim().replace(/·\s*$/, '') : null;
        const matCount = lesson.materials?.length || 0;

        return (
          <React.Fragment key={lesson.id}>
            {idx > 0 && <div style={{ height: 1, background: 'rgba(124,77,255,0.08)', margin: '2px 0' }} />}
            <div
              {...getItemProps(lesson.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 8px', borderRadius: 10, transition: 'background 0.12s', cursor: 'pointer' }}
              onClick={() => handleEdit(lesson)}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,77,255,0.07)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <DateBadge dateStr={lesson.date} />

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {lesson.title}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 3, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {dayLabel && <span>{dayLabel}</span>}
                  {matCount > 0 && <span>{matCount} материал{matCount === 1 ? '' : matCount < 5 ? 'а' : 'ов'}</span>}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                <button
                  onClick={(e) => { e.stopPropagation(); handleEdit(lesson); }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '5px 10px',
                    border: '1px solid var(--border)', borderRadius: 8,
                    background: 'var(--card)', color: 'var(--text-2)',
                    fontSize: 12, fontWeight: 600,
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(124,77,255,0.45)'; e.currentTarget.style.color = '#7c4dff'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-2)'; }}
                >
                  <IcoEdit /> Ред.
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(lesson.id); }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 30, height: 30,
                    border: '1px solid #fecaca', borderRadius: 8,
                    background: 'rgba(239,68,68,0.13)', color: '#dc2626',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.20)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(239,68,68,0.13)'}
                >
                  <IcoTrash />
                </button>
                <DragHandle {...getHandleProps(lesson.id)} />
              </div>
            </div>
          </React.Fragment>
        );
      })}

      {/* Add lesson */}
      <button
        onClick={handleAdd}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '8px 14px',
          margin: '6px 0 2px 58px',
          border: '1.5px dashed #c7d2fe', borderRadius: 10,
          fontSize: 12, fontWeight: 600, color: '#7c4dff',
          background: 'none', cursor: 'pointer',
          transition: 'all 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(124,77,255,0.15)'; e.currentTarget.style.borderStyle = 'solid'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderStyle = 'dashed'; }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        Добавить урок
      </button>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} bare>
        <div className="adm-modal w-lesson">
          <div className="adm-modal-head">
            <div className="adm-head-ic">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
            </div>
            <div className="adm-head-tt">
              <div className="adm-modal-title">{editingLesson ? 'Редактировать урок' : 'Новый урок'}</div>
              <div className="adm-modal-desc">Ссылки, материалы, дата и время</div>
            </div>
            <button className="adm-x" aria-label="Закрыть" onClick={() => setModalOpen(false)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

          <div className="adm-modal-body">
            <div className="fl">
              <label className="fl-label">Название урока</label>
              <input className="fl-input" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} />
            </div>

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border)', background: formData.isHomeworkOnly ? 'var(--primary-light)' : 'var(--bg)', cursor: 'pointer', marginBottom: 4 }}>
              <input type="checkbox" checked={!!formData.isHomeworkOnly} onChange={e => setFormData({ ...formData, isHomeworkOnly: e.target.checked })} style={{ marginTop: 2, accentColor: 'var(--primary)', width: 16, height: 16, flexShrink: 0 }} />
              <span>
                <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-1)' }}>ДЗ без вебинара</span>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>Урок не показывается в «Вебинарах», а у его ДЗ нет кнопки «К уроку». Ссылку/запись/дату можно не заполнять.</span>
              </span>
            </label>

            {/* Поля «Ссылка на урок» больше нет: ученик заходит по личной ссылке из профиля */}
            <div className="fl">
              <label className="fl-label">
                <span className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg></span>
                Ссылка на запись
              </label>
              <div className="fl-iwrap">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
                <input className="fl-input with-ic" value={formData.recordLink || ''} onChange={e => setFormData({ ...formData, recordLink: e.target.value })} />
              </div>
            </div>

            {formData.recordLink && (
              <div className="fl">
                <label className="fl-label">
                  <span className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg></span>
                  Таймкоды записи <span className="opt">— главы для ученика</span>
                </label>
                <TimecodeEditor
                  recordLink={formData.recordLink}
                  value={formData.timecodes}
                  onChange={(tc) => setFormData({ ...formData, timecodes: tc })}
                />
              </div>
            )}

            <div className="materials">
              <div className="materials-head2">
                <h4><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>Материалы к уроку</h4>
                <button className="add-file-btn2" onClick={() => document.getElementById('fileInput').click()} disabled={!editingLesson || uploading}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  {uploading ? 'Загрузка...' : 'Добавить'}
                </button>
                <input type="file" id="fileInput" style={{ display: 'none' }} multiple onChange={e => handleFileUpload(e.target.files)} disabled={!editingLesson} />
              </div>
              <div className="mat-list2">
                {formData.materials?.map((material, index) => {
                  const nm = material.filename || material.name || material.title || material;
                  const sz = material.size ? `${(material.size / 1048576).toFixed(1).replace('.', ',')} МБ` : '';
                  return (
                    <div key={index} className="mat-item2">
                      <span className="mat-ic2" style={{ background: fileColor(nm)[0], color: fileColor(nm)[1] }}><FileIcon name={nm} size={18} /></span>
                      <span className="mat-name2">{nm}</span>
                      {sz && <span className="mat-size2">{sz}</span>}
                      <button className="mat-del2" aria-label="Удалить" onClick={() => handleRemoveMaterial(index)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                      </button>
                    </div>
                  );
                })}
                {(!formData.materials || formData.materials.length === 0) && (
                  <div className="mat-empty2">{editingLesson ? 'Нет загруженных материалов' : 'Сохраните урок, чтобы добавить материалы'}</div>
                )}
              </div>
            </div>

            <div className="fl-grid2">
              <div className="fl">
                <label className="fl-label"><span className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></span>Дата проведения</label>
                <DatePicker value={formData.date || ''} onChange={(v) => setFormData({ ...formData, date: v })} />
              </div>
              <div className="fl">
                <label className="fl-label"><span className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg></span>Время</label>
                <TimePicker value={formData.time || ''} onChange={(v) => setFormData({ ...formData, time: v })} />
              </div>
            </div>

            <div className="fl">
              <label className="fl-label"><span className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 8 12 12 14 13"/></svg></span>Длительность урока, минут</label>
              <input className="fl-input" type="number" min="1" step="5" value={formData.duration ?? ''} onChange={e => setFormData({ ...formData, duration: e.target.value })} />
              <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 6 }}>
                Определяет, сколько урок считается «в эфире» (доступен вход). Если пусто — 120 минут.
              </div>
            </div>
          </div>

          <div className="adm-modal-foot">
            <span className="adm-foot-hint"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>Изменения сохранятся для всех учеников</span>
            <button className="adm-btn adm-btn-sec" onClick={() => setModalOpen(false)}>Отмена</button>
            <button className="adm-btn adm-btn-pri" onClick={handleSubmit}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              {editingLesson ? 'Сохранить' : 'Создать урок'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LessonsManager;
